// A live input level bar, so people can see their mic works before joining.

import { useEffect, useRef } from 'react';

/** RMS of a block of samples, mapped to 0–1 on a dB scale (−60 dB … 0 dB). */
export function levelOf(samples: Float32Array): number {
  let sum = 0;
  for (const s of samples) sum += s * s;
  const rms = Math.sqrt(sum / Math.max(1, samples.length));
  if (rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  return Math.min(1, Math.max(0, (db + 60) / 60));
}

export function MicMeter({ stream, AudioContextImpl }: { stream: MediaStream | null; AudioContextImpl?: typeof AudioContext }) {
  const bar = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0) return;
    // No Web Audio (old or locked-down browsers): no meter, nothing breaks.
    const Ctx = AudioContextImpl ?? (typeof AudioContext === 'undefined' ? null : AudioContext);
    if (!Ctx) return;
    const ctx = new Ctx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    let frame = 0;
    const tick = () => {
      analyser.getFloatTimeDomainData(buf);
      const level = levelOf(buf);
      if (bar.current) {
        bar.current.style.width = `${Math.round(level * 100)}%`;
        bar.current.dataset.level = level.toFixed(2);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      void ctx.close();
    };
  }, [stream, AudioContextImpl]);

  return (
    <div role="meter" aria-label="Microphone level" aria-valuemin={0} aria-valuemax={100} className="h-2 overflow-hidden rounded-full bg-surface-2">
      <div ref={bar} className="h-full w-0 bg-success transition-[width] duration-75" />
    </div>
  );
}
